import React, { useState, useRef, useEffect } from 'react';
import { RotateCcw, Trophy, Zap, Play } from 'lucide-react';

const ALVO_IMG =
  'https://customer-assets-wrfwihn1.emergentagent.net/job_darts-responsive/artifacts/vz334565_ALVO.webp';

// Standard dartboard segments in clockwise order starting at 20 (top)
const SEGMENTS = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

// Compute dart score based on click position relative to board center (values are ratios of maxR)
function computeScore(nx, ny) {
  const r = Math.sqrt(nx * nx + ny * ny); // 0..~1 (>1 = outside)

  if (r > 1.0) return { score: 0, label: 'FORA', color: '#888' };
  if (r < 0.045) return { score: 50, label: 'BULLSEYE 50', color: '#ff4d5a' };
  if (r < 0.095) return { score: 25, label: 'BULL 25', color: '#22e6e6' };

  // Angle in degrees, 0 at top (12h), clockwise
  let deg = (Math.atan2(nx, -ny) * 180) / Math.PI; // atan2(x, -y) => 0 at top
  if (deg < 0) deg += 360;
  const idx = Math.floor(((deg + 9) % 360) / 18); // segments are 18° wide, first centered at 0
  const segValue = SEGMENTS[idx];

  // Rings on real dartboards (normalized to outer double edge = 1):
  // triple: ~0.58-0.63 ; double: ~0.94-1.0
  let mult = 1;
  let ring = '';
  if (r >= 0.94) {
    mult = 2;
    ring = 'DBL';
  } else if (r >= 0.58 && r <= 0.63) {
    mult = 3;
    ring = 'TRIPLE';
  }
  const score = segValue * mult;
  return {
    score,
    label: `${ring ? ring + ' ' : ''}${segValue}${mult > 1 ? ' ×' + mult : ''} = ${score}`,
    color: mult >= 2 ? '#ff4d5a' : '#22e6e6',
  };
}

export default function PlayGame() {
  const boardRef = useRef(null);
  const [darts, setDarts] = useState([]); // { x, y, score, label, color }
  const [lastHit, setLastHit] = useState(null);
  const [best, setBest] = useState(
    () => Number(localStorage.getItem('sd_best_round') || 0),
  );
  const [totalGames, setTotalGames] = useState(
    () => Number(localStorage.getItem('sd_total_games') || 0),
  );
  const [roundOver, setRoundOver] = useState(false);
  const [difficulty, setDifficulty] = useState('normal'); // easy, normal, pro

  const jitterMap = { easy: 0.015, normal: 0.045, pro: 0.09 };

  const roundTotal = darts.reduce((s, d) => s + d.score, 0);
  const dartsLeft = 3 - darts.length;

  const handleThrow = (e) => {
    if (darts.length >= 3 || !boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Aim jitter (adds a small random offset)
    const jitter = jitterMap[difficulty];
    const jx = (Math.random() - 0.5) * jitter * rect.width;
    const jy = (Math.random() - 0.5) * jitter * rect.width;
    const x = clickX + jx;
    const y = clickY + jy;

    // Normalize to board coords: dartboard occupies ~92% of container
    const maxR = rect.width * 0.46;
    const nx = (x - cx) / maxR;
    const ny = (y - cy) / maxR;

    const result = computeScore(nx, ny);
    const newDart = { x, y, ...result };
    const newDarts = [...darts, newDart];
    setDarts(newDarts);
    setLastHit(result);

    if (newDarts.length === 3) {
      const total = newDarts.reduce((s, d) => s + d.score, 0);
      setRoundOver(true);
      setTotalGames((g) => {
        const nv = g + 1;
        localStorage.setItem('sd_total_games', String(nv));
        return nv;
      });
      if (total > best) {
        setBest(total);
        localStorage.setItem('sd_best_round', String(total));
      }
    }
  };

  const resetRound = () => {
    setDarts([]);
    setLastHit(null);
    setRoundOver(false);
  };

  return (
    <section id="jogar" className="relative w-full py-20 md:py-28 bg-[color:var(--sd-bg)] overflow-hidden">
      <div className="absolute inset-0 sd-grid-bg opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="flex items-center gap-3 text-[color:var(--sd-cyan)] text-xs tracking-[0.35em] font-mono">
          <Zap size={14} /> MODO VIRTUAL // BETA
        </div>
        <h2 className="mt-4 font-display font-black text-white text-3xl md:text-5xl tracking-widest leading-tight">
          ENQUANTO ESPERAS POR{' '}
          <span className="text-[color:var(--sd-cyan)] sd-glow">2027…</span>
          <br />JOGA AQUI
        </h2>
        <p className="mt-4 font-mono text-[color:var(--sd-text)]/80 max-w-2xl leading-relaxed">
          Modo virtual com alvo de plástico soft-tip. Clica no alvo para arremessar
          — tens 3 dardos por ronda. Testa a tua pontaria!
        </p>
        <div className="sd-rule mt-6 mb-10" />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
          {/* Board */}
          <div className="sd-panel sd-brackets p-6 md:p-10">
            <div className="flex items-center justify-between mb-4 text-[10px] tracking-[0.3em] font-mono">
              <span className="text-[color:var(--sd-cyan)]/70">// PLAY_AREA_01</span>
              <div className="flex gap-2">
                {['easy', 'normal', 'pro'].map((d) => (
                  <button
                    key={d}
                    onClick={() => {
                      setDifficulty(d);
                      resetRound();
                    }}
                    className={`px-2.5 py-1 border tracking-[0.2em] transition-colors ${
                      difficulty === d
                        ? 'bg-[color:var(--sd-cyan)] text-black border-[color:var(--sd-cyan)]'
                        : 'border-[rgba(34,230,230,0.3)] text-[color:var(--sd-cyan)] hover:border-[color:var(--sd-cyan)]'
                    }`}
                  >
                    {d.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div
              ref={boardRef}
              onClick={handleThrow}
              className={`relative mx-auto aspect-square w-full max-w-[520px] select-none ${
                darts.length < 3 ? 'cursor-crosshair' : 'cursor-not-allowed'
              }`}
              style={{
                background:
                  'radial-gradient(circle at center, rgba(34,230,230,0.15) 0%, rgba(34,180,255,0.05) 40%, rgba(5,6,8,0) 70%)',
              }}
            >
              {/* Neon halo */}
              <div
                className="absolute -inset-6 rounded-full pointer-events-none"
                style={{ boxShadow: '0 0 60px 12px rgba(34,230,230,0.25)' }}
              />
              <img
                src={ALVO_IMG}
                alt="Alvo soft-tip"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                draggable={false}
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(34,230,230,0.4))',
                }}
              />
              {/* Darts landed */}
              {darts.map((d, i) => (
                <div
                  key={i}
                  className="absolute pointer-events-none"
                  style={{
                    left: d.x,
                    top: d.y,
                    transform: 'translate(-50%, -50%)',
                    animation: 'sd-dart-hit 0.4s ease-out',
                  }}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      background: d.color,
                      boxShadow: `0 0 14px ${d.color}, 0 0 24px ${d.color}`,
                    }}
                  />
                  <div
                    className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-mono tracking-[0.15em]"
                    style={{ color: d.color }}
                  >
                    +{d.score}
                  </div>
                </div>
              ))}
              {/* Crosshair when idle */}
              {darts.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-[color:var(--sd-cyan)]/80 text-xs tracking-[0.35em] font-mono bg-black/40 px-3 py-1.5 border border-[rgba(34,230,230,0.3)]">
                    CLICA PARA ARREMESSAR
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scoreboard */}
          <div className="space-y-5">
            <div className="sd-panel sd-brackets p-6">
              <div className="text-[10px] tracking-[0.3em] text-[color:var(--sd-cyan)]/70 font-mono mb-2">
                // RONDA ATUAL
              </div>
              <div className="font-display font-black text-[color:var(--sd-cyan)] text-6xl sd-glow leading-none">
                {roundTotal}
              </div>
              <div className="mt-4 flex items-center justify-between font-mono text-sm">
                <span className="text-[color:var(--sd-text)]/70">Dardos:</span>
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className={`w-3 h-3 rounded-full border ${
                        i < darts.length
                          ? 'bg-[color:var(--sd-cyan)] border-[color:var(--sd-cyan)] shadow-[0_0_8px_var(--sd-cyan)]'
                          : 'border-[rgba(34,230,230,0.3)]'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between font-mono text-sm">
                <span className="text-[color:var(--sd-text)]/70">Restam:</span>
                <span className="text-[color:var(--sd-cyan)]">{dartsLeft}</span>
              </div>
            </div>

            {lastHit && (
              <div className="sd-panel sd-brackets p-4">
                <div className="text-[10px] tracking-[0.3em] text-[color:var(--sd-cyan)]/70 font-mono mb-1">
                  // ÚLTIMO ARREMESSO
                </div>
                <div
                  className="font-mono text-lg tracking-[0.15em]"
                  style={{ color: lastHit.color }}
                >
                  {lastHit.label}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="sd-panel sd-brackets p-4 text-center">
                <Trophy size={16} className="mx-auto text-[color:var(--sd-cyan)]" />
                <div className="font-display font-black text-white text-2xl mt-1">
                  {best}
                </div>
                <div className="text-[9px] tracking-[0.3em] text-[color:var(--sd-muted)] font-mono">
                  MELHOR
                </div>
              </div>
              <div className="sd-panel sd-brackets p-4 text-center">
                <Play size={16} className="mx-auto text-[color:var(--sd-cyan)]" />
                <div className="font-display font-black text-white text-2xl mt-1">
                  {totalGames}
                </div>
                <div className="text-[9px] tracking-[0.3em] text-[color:var(--sd-muted)] font-mono">
                  RONDAS
                </div>
              </div>
            </div>

            <button
              onClick={resetRound}
              className="sd-btn w-full"
              disabled={darts.length === 0}
            >
              <RotateCcw size={14} /> NOVA RONDA
            </button>

            {roundOver && (
              <div className="sd-panel sd-brackets p-4 border-[color:var(--sd-cyan)]">
                <div className="font-display text-[color:var(--sd-cyan)] tracking-widest text-sm sd-glow">
                  RONDA COMPLETA
                </div>
                <div className="font-mono text-sm text-[color:var(--sd-text)]/85 mt-1">
                  {roundTotal > best - 1 && darts.length === 3 && roundTotal === best
                    ? '🏁 NOVO RECORDE PESSOAL!'
                    : `Total: ${roundTotal} pontos. Tenta melhorar!`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
